import React, { useMemo, useState } from 'react';
import { Alert, Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { FilterYear } from '../../components/Reports/FilterYear';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';
import { Waiting } from '../../../common/components/Waiting/Waiting';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { FilterYearReportData } from '../../components/Reports/types/FilterYearTypes';
import { DownloadDocumentsSearch } from '../../components/DownloadDocuments/DownloadDocumentsSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';

interface BulkDownloadDocumentsViewProps {
  withProjectAttachments?: boolean;
  withContracts?: boolean;
  withAttachmentsTypes?: boolean;
  filterOnlyCurrentUser?: boolean;
  withoutCompany?: boolean;
}

export const BulkDownloadDocumentsView: React.FC<
  BulkDownloadDocumentsViewProps
> = ({
  withProjectAttachments = true,
  withContracts = true,
  withAttachmentsTypes = true,
  filterOnlyCurrentUser = false,
  withoutCompany = false,
}) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [waiting, setWaiting] = useState<boolean>(false);
  const [alert, setAlert] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { t, tk } = useTranslation('commonLng');

  const onSearch = async (filter: FilterYearReportData) => {
    if (
      !filterOnlyCurrentUser &&
      !filter.budget &&
      !filter.clientId &&
      !filter.teamId &&
      !filter.project
    ) {
      setError(t(tk.download.needAtLeastOneFilter));
      return;
    }

    if (!filter.rangeMonthFrom || !filter.rangeMonthTo) {
      setError(t(tk.download.rangeMustBeDefined));
      return;
    }
    if (filterOnlyCurrentUser && filter.rangeMonthFrom && filter.rangeMonthTo) {
      const fromDate = new Date(filter.rangeMonthFrom);
      const toDate = new Date(filter.rangeMonthTo);

      if (fromDate.getFullYear() !== toDate.getFullYear()) {
        setError(t(tk.download.rangeMustBeInSameYear));
        return;
      }
    }

    if (
      !filter.salesInvoices &&
      !filter.salesInvoicesCopies &&
      !filter.purchaseInvoices &&
      (filterOnlyCurrentUser ||
        (!filter.projectAttachments && !filter.contracts))
    ) {
      setError(t(tk.download.needSomeTypeDocuments));
      return;
    }
    setAlert(t(tk.download.waitingDataDownload));
    setError('');
    setWaiting(true);

    try {
      const filterToSend = {
        ...filter,
        ...(filterOnlyCurrentUser && {
          projectAttachments: false,
          contracts: false,
        }),
      };
      await pok.jobConfig.bulkDownloadsDocuments(filterToSend);
      setWaiting(false);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
      setWaiting(false);
    }
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.downloadDoc.getAllByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          filterOnlyCurrentUser,
        )) as GridGetterFunction,
    [pok.downloadDoc, t, tk, filterOnlyCurrentUser],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.download.downloadDoc)}</Card.Title>
        <TabsHistory
          activeKey="params-tabs"
          id="download_documents"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="params-tabs"
            title={t(tk.download.newDownloadDoc)}
            className="m-3"
          >
            <Alert variant="info">
              {filterOnlyCurrentUser
                ? t(tk.download.downloadMyInfo)
                : t(tk.download.downloadInfo)}
            </Alert>
            <FilterYear
              onSearch={onSearch}
              reportName={t(tk.download.bulkDownloadsDocuments)}
              withBudget={true}
              withRangeMonth={true}
              withTeam={true}
              withSalesInvoices={true}
              withSalesInvoicesCopies={true}
              withContracts={withContracts}
              withProjectAttachments={withProjectAttachments}
              withPurchaseInvoices={true}
              withAttachmentsTypes={withAttachmentsTypes}
              withProjects={true}
              withYearType={true}
              withoutDateOfProject={true}
              withClient={true}
              withoutCompany={withoutCompany}
            />
            {error && <Alert variant="danger">{error}</Alert>}
            {waiting && (
              <>
                <Alert variant="warning">{alert}</Alert>
                <Waiting />
              </>
            )}
          </Tab>
          <Tab
            eventKey="all_invoices"
            title={
              filterOnlyCurrentUser
                ? t(tk.download.downloadedMyDoc)
                : t(tk.download.downloadedDoc)
            }
            className="m-3"
          >
            <DownloadDocumentsSearch
              getData={getData}
              getCount={(filterText, filterColumns) =>
                pok.downloadDoc.getAllCount(
                  filterText,
                  filterColumns,
                  filterOnlyCurrentUser,
                )
              }
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
